import { userHasAccess } from "@/access-control/permissions";
import usePermissions from "@/access-control/queries/usePermissions";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import useUsers from "@/hooks/useUsers";
import type { Feature } from "@/types/feature";

export type FeatureAccessLevel = "none" | "read" | "write";

export type UseHasRBACAccessToFeatureResult = {
  level: FeatureAccessLevel;
  loading: boolean;
};

const featureDomains: Record<Feature, RBACDomain> = {
  ai_chat: RBACDomain.freshpaintAI,
  funnels: RBACDomain.analytics,
  web_analytics: RBACDomain.analytics,
  surveys: RBACDomain.heatmapsSurveysAndSessionReplay,
  heatmaps: RBACDomain.heatmapsSurveysAndSessionReplay,
  session_replay: RBACDomain.heatmapsSurveysAndSessionReplay,
  segments: RBACDomain.audiences,
  // prospecting_audiences sets requiresRBAC: false in the FEATURES registry, so
  // this entry is never consulted at runtime — it only satisfies the
  // Record<Feature, RBACDomain> exhaustiveness check. audiences is the right
  // sibling domain in case we ever flip requiresRBAC to true.
  prospecting_audiences: RBACDomain.audiences,
  // Personas ride on Freshpaint AI access. The personas feature config sets
  // requiresRBAC: true, so this domain gates the page: roles without
  // freshpaintai access (data_viewer, event_manager) get the no-permissions
  // splash, matching the backend's RequireAIAccessV1 gate.
  personas: RBACDomain.freshpaintAI,
};

export function useHasRBACAccessToFeature(
  feature: Feature,
): UseHasRBACAccessToFeatureResult {
  const usersState = useUsers();
  const { data: rolePermissions, isLoading: permsLoading } = usePermissions();

  if (permsLoading || usersState.loading) {
    return { level: "none", loading: true };
  }

  const domain = featureDomains[feature];
  const canWrite = userHasAccess(
    usersState.currentUser,
    rolePermissions,
    domain,
    RBACAccessLevel.write,
  );
  if (canWrite === true) {
    return { level: "write", loading: false };
  }

  const canRead = userHasAccess(
    usersState.currentUser,
    rolePermissions,
    domain,
    RBACAccessLevel.read,
  );
  if (canRead === true) {
    return { level: "read", loading: false };
  }

  return { level: "none", loading: false };
}
